/**
 * One family's editor — its view, parameters, RFA binding, conversation and
 * versions. `bimgo-ai-web`'s `useFamilyAuthoringController`, rebuilt on this
 * app's turn shape (`ChatMessage`: text + reasoning + steps) so the transcript
 * renders with the chat's own components.
 *
 * The v2 fixes kept, each with its reason:
 *
 * - **Refresh after every turn, and right after every Build.** A turn may save a
 *   version and then end on an agent error; the editor reconciles with disk
 *   whatever happened, rather than showing code and versions from before the turn.
 *   The Build event arrives after the versioning attempt, so Versions refresh then
 *   too, even while the agent carries on editing.
 * - **Recover after a failed turn.** The reply keeps whatever it produced, the
 *   failure is said on it, and an RFA refusal moves the banner to the state that
 *   names the way back (Retry / Locate).
 * - **Parameter runs stay out of the chat.** Applying parameters is not a turn:
 *   it updates the view and the run summary, never the transcript.
 * - **Stale answers are dropped.** Every async result is checked against the
 *   identity it was asked under (artifact, instance, org, sign-in), so switching
 *   family or Revit mid-request cannot paint the old one's data over the new.
 *
 * Stop is the chat's: the fetch is aborted **and** the agent is told (`/abort`
 * with the thread id), so the turn stops driving Revit rather than only stopping
 * being read. Leaving the page does not stop a turn, as in v2.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { API_BASE_URL } from "../lib/config";
import { abortTurn, type AgentStep, type ContextUsage } from "../lib/chat";
import { describeAskError } from "../lib/askError";
import { sentAttachment, sentAttachmentsFromUrls, storedMessageText, type Attachment } from "../lib/attachments";
import { getThreadMessages, type ThreadMessage } from "../lib/threads";
import { createSkillsClient, type ExecutionDoc } from "../lib/skills";
import type { ChatMessage } from "../hooks/useChatTurn";
import { createFamilyBuilderClient, streamFamilyTurn, type FamilyBuilderClient } from "./api";
import { familyDocumentErrorKey, FamilyBuilderError, bindingFailure, shouldLocate, type BindingFailure } from "./errors";
import {
  activityLabelKey,
  applyCurrentView,
  hasParameterChanges as computeParameterChanges,
  parameterValues,
  parametersFromView,
  successfulTurnRefreshStatus,
} from "./model";
import type {
  FamilyBuildEvidence,
  FamilyExecutionResponse,
  FamilyParameter,
  FamilyRetryAction,
  FamilyRfaBinding,
  FamilyRfaBindingState,
  FamilyView,
} from "./types";

export type FamilyStatus = "idle" | "loading_history" | "running" | "error";

/** A transcript row, with the Build it produced (the last one, as v2 shows it). */
export type FamilyMessage = ChatMessage & { evidence?: FamilyBuildEvidence };

type T = (key: string, vars?: Record<string, string | number>) => string;

let counter = 0;
const nextId = (prefix: string) => `${prefix}-fb-${++counter}`;

function historyToMessages(rows: readonly ThreadMessage[]): FamilyMessage[] {
  return rows
    .filter((row) => (typeof row.content === "string" && row.content.trim()) || row.images?.length)
    .map((row) => {
      const attachments = sentAttachmentsFromUrls(row.images);
      return {
        id: `h-${row.id}`,
        role: row.sender === "user" ? ("user" as const) : ("assistant" as const),
        text: storedMessageText(row.content, attachments),
        ...(attachments.length > 0 ? { attachments } : {}),
      };
    });
}

export interface FamilyBuilderOptions {
  artifactId: string;
  token: string | null;
  orgId: string | null;
  targetSessionId: string | null;
  projectId: string | null;
  userId: string | null;
  model: { provider: string; model: string } | null;
  reasoningEffort?: string | undefined;
  t: T;
}

export function useFamilyBuilder(options: FamilyBuilderOptions) {
  const { artifactId, token, orgId, targetSessionId, projectId, userId, model, reasoningEffort, t } = options;
  const [threadId, setThreadIdState] = useState<string | null>(null);
  const threadIdRef = useRef<string | null>(null);
  const [messages, setMessages] = useState<FamilyMessage[]>([]);
  const [view, setView] = useState<FamilyView | null>(null);
  const [parameters, setParameters] = useState<FamilyParameter[]>([]);
  const [status, setStatus] = useState<FamilyStatus>("loading_history");
  const [error, setError] = useState<string | null>(null);
  const [parameterExecutionError, setParameterExecutionError] = useState<string | null>(null);
  const [versionSaveError, setVersionSaveError] = useState<string | null>(null);
  const [isApplyingParameters, setIsApplyingParameters] = useState(false);
  const [lastExecution, setLastExecution] = useState<FamilyExecutionResponse | null>(null);
  const [rfaBinding, setRfaBinding] = useState<FamilyRfaBinding | null>(null);
  const [rfaBindingState, setRfaBindingState] = useState<FamilyRfaBindingState>("activating");
  const [rfaBindingError, setRfaBindingError] = useState<string | null>(null);
  const [rfaBindingPath, setRfaBindingPath] = useState<string | null>(null);
  const [rfaBindingRetryAction, setRfaBindingRetryAction] = useState<FamilyRetryAction | undefined>();
  const [rfaBindingCode, setRfaBindingCode] = useState<string | null>(null);
  const [usage, setUsage] = useState<ContextUsage | null>(null);
  /** Bumped whenever the version list may have changed — the Versions panel refetches. */
  const [versionsKey, setVersionsKey] = useState(0);
  /** A one-off confirmation for the toast. */
  const [notice, setNotice] = useState<{ tone: "ok" | "err"; text: string } | null>(null);

  const abortRef = useRef<AbortController | null>(null);
  const tRef = useRef(t);
  tRef.current = t;
  const tokenRef = useRef(token);
  tokenRef.current = token;

  const client: FamilyBuilderClient | null = useMemo(
    () => (token && orgId && targetSessionId ? createFamilyBuilderClient({ get token() { return tokenRef.current!; }, orgId, targetSessionId }) : null),
    [Boolean(token), orgId, targetSessionId, userId],
  );

  const openingIdentity = JSON.stringify([artifactId, targetSessionId, orgId, userId, Boolean(token)]);
  const identityRef = useRef(openingIdentity);
  identityRef.current = openingIdentity;

  // --- the run summary: the latest recorded run, as v2's `latestOnly` history panel -------
  // Every Build and Apply is recorded under the artifact id in the local run history, so
  // the summary survives a reload and shows runs the agent made too. Read through the
  // skills client's run endpoints, which serve any id from that history (2.x did the same).
  const [latestRun, setLatestRun] = useState<ExecutionDoc | null>(null);
  const loadLatestRun = useCallback(async (): Promise<void> => {
    if (!tokenRef.current || !orgId) return;
    const runsClient = createSkillsClient({ token: tokenRef.current, orgId });
    const identity = identityRef.current;
    try {
      const [latest] = await runsClient.executions(artifactId, 1);
      const doc = latest ? await runsClient.execution(artifactId, latest.executionId) : null;
      if (identityRef.current === identity) setLatestRun(doc);
    } catch {
      // The summary keeps what it shows; the run history is a convenience, not the editor.
    }
  }, [orgId, artifactId]);
  const openingRequestRef = useRef<object | null>(null);
  const viewRefreshRequestRef = useRef<object | null>(null);
  const turnRequestRef = useRef<object | null>(null);

  const assignThreadId = useCallback((next: string | null) => {
    threadIdRef.current = next;
    setThreadIdState(next);
  }, []);

  const applyView = useCallback(
    (next: FamilyView) => {
      setView(next);
      setParameters(parametersFromView(next));
      assignThreadId(next.versioning.regen_thread_id ?? null);
    },
    [assignThreadId],
  );

  const applyBindingFailure = useCallback((failure: BindingFailure, keepPath?: string | null) => {
    setRfaBindingState(failure.state);
    const key = familyDocumentErrorKey(failure.message, failure.code);
    setRfaBindingError(key ? tRef.current(key) : failure.message);
    setRfaBindingPath(failure.path ?? keepPath ?? null);
    setRfaBindingRetryAction(failure.retryAction);
    setRfaBindingCode(failure.code);
  }, []);

  const applyBinding = useCallback((binding: FamilyRfaBinding) => {
    setRfaBinding(binding);
    setRfaBindingPath(binding.documentPath);
    setRfaBindingState("ready");
    setRfaBindingError(null);
    setRfaBindingRetryAction(undefined);
    setRfaBindingCode(null);
  }, []);

  const refreshView = useCallback(async (): Promise<boolean> => {
    if (!client || identityRef.current !== openingIdentity) return false;
    const request = {};
    viewRefreshRequestRef.current = request;
    return applyCurrentView(
      () => client.view(artifactId),
      () => identityRef.current === openingIdentity && viewRefreshRequestRef.current === request,
      applyView,
    );
  }, [client, artifactId, openingIdentity, applyView]);

  const loadHistory = useCallback(async (id: string): Promise<FamilyMessage[]> => {
    const rows = await getThreadMessages(tokenRef.current, id, { apiBaseUrl: API_BASE_URL() });
    return historyToMessages(rows);
  }, []);

  // --- opening: activate the RFA and read the view, side by side (v2) ------------------
  useEffect(() => {
    turnRequestRef.current = null;
    viewRefreshRequestRef.current = null;
    setMessages([]);
    setView(null);
    setParameters([]);
    setError(null);
    setParameterExecutionError(null);
    setVersionSaveError(null);
    setIsApplyingParameters(false);
    setLastExecution(null);
    setLatestRun(null);
    setRfaBinding(null);
    setRfaBindingState("activating");
    setRfaBindingError(null);
    setRfaBindingPath(null);
    setRfaBindingRetryAction(undefined);
    setRfaBindingCode(null);
    setUsage(null);
    setStatus("loading_history");
    assignThreadId(null);

    if (!client) {
      setStatus("error");
      setError(tRef.current("familyBuilder.missingSession"));
      return;
    }

    let cancelled = false;
    const isCurrentIdentity = () => !cancelled && identityRef.current === openingIdentity;
    const openingRequest = {};
    openingRequestRef.current = openingRequest;
    const isCurrentOpening = () => isCurrentIdentity() && openingRequestRef.current === openingRequest;

    void client
      .activate(artifactId)
      .then((binding) => {
        if (isCurrentOpening()) applyBinding(binding);
      })
      .catch((activationError: unknown) => {
        if (isCurrentOpening()) applyBindingFailure(bindingFailure(activationError));
      })
      .finally(() => {
        if (openingRequestRef.current === openingRequest) openingRequestRef.current = null;
      });

    void loadLatestRun();

    void client
      .view(artifactId)
      .then(async (restored) => {
        if (!isCurrentIdentity()) return;
        applyView(restored);
        const active = restored.versioning.regen_thread_id;
        if (active) {
          try {
            const history = await loadHistory(active);
            if (isCurrentIdentity()) setMessages(history);
          } catch {
            // No history is an empty transcript, not a broken editor: the thread
            // may be someone else's or gone, and the next turn starts a new one.
          }
        }
        if (isCurrentIdentity()) setStatus("idle");
      })
      .catch((loadError: unknown) => {
        if (!isCurrentIdentity()) return;
        setStatus("error");
        setError(loadError instanceof Error ? loadError.message : String(loadError));
      });

    return () => {
      if (abortRef.current) {
        abortRef.current.abort();
        const stopToken = identityRef.current === openingIdentity ? tokenRef.current : token;
        if (threadIdRef.current && stopToken) void abortTurn(threadIdRef.current, stopToken).catch(() => undefined);
        abortRef.current = null;
      }
      cancelled = true;
      openingRequestRef.current = null;
      turnRequestRef.current = null;
      viewRefreshRequestRef.current = null;
    };
  }, [client, artifactId, openingIdentity, applyView, applyBinding, applyBindingFailure, assignThreadId, loadHistory, loadLatestRun]);

  const isRunning = status === "running";
  const isBusy = isRunning || isApplyingParameters;

  // --- Retry / Locate -------------------------------------------------------------------
  const openRfaInRevit = useCallback(async () => {
    if (!client || openingRequestRef.current || rfaBindingState === "activating" || isBusy) return;
    if (rfaBindingRetryAction === "none") return;
    const locate = shouldLocate(rfaBindingState, rfaBindingRetryAction);
    const openingRequest = {};
    openingRequestRef.current = openingRequest;
    const isCurrentOpening = () => identityRef.current === openingIdentity && openingRequestRef.current === openingRequest;
    const previous = { state: rfaBindingState, error: rfaBindingError };
    try {
      setRfaBindingState("activating");
      setRfaBindingError(null);
      const binding = await (locate ? client.locate(artifactId) : client.activate(artifactId));
      if (isCurrentOpening()) {
        applyBinding(binding);
        setError(null);
        setParameterExecutionError(null);
        setNotice(null);
      }
    } catch (bindingError) {
      if (!isCurrentOpening()) return;
      const failure = bindingFailure(bindingError);
      // Cancelling the file picker changes nothing: the banner goes back to what it said.
      if (failure.state === "cancelled") {
        setRfaBindingState(previous.state);
        setRfaBindingError(previous.error);
        return;
      }
      applyBindingFailure(failure, rfaBindingPath);
    } finally {
      if (openingRequestRef.current === openingRequest) openingRequestRef.current = null;
    }
  }, [client, artifactId, openingIdentity, rfaBindingState, rfaBindingError, rfaBindingPath, rfaBindingRetryAction, isBusy, applyBinding, applyBindingFailure]);

  // --- threads --------------------------------------------------------------------------
  const newThread = useCallback(async () => {
    if (!client || isBusy) return;
    setError(null);
    try {
      setStatus("loading_history");
      await client.setThread(artifactId, null);
      assignThreadId(null);
      setMessages([]);
      setUsage(null);
      setView((current) => (current ? { ...current, versioning: { ...current.versioning, regen_thread_id: null } } : current));
      setStatus("idle");
    } catch (threadError) {
      setStatus("error");
      setError(threadError instanceof Error ? threadError.message : String(threadError));
    }
  }, [client, artifactId, isBusy, assignThreadId]);

  const switchThread = useCallback(
    async (nextThreadId: string) => {
      if (!client || !nextThreadId || nextThreadId === threadIdRef.current || isBusy) return;
      try {
        setStatus("loading_history");
        setError(null);
        await client.setThread(artifactId, nextThreadId);
        const history = await loadHistory(nextThreadId);
        assignThreadId(nextThreadId);
        setMessages(history);
        setUsage(null);
        setView((current) =>
          current ? { ...current, versioning: { ...current.versioning, regen_thread_id: nextThreadId } } : current,
        );
        setStatus("idle");
      } catch (threadError) {
        setStatus("error");
        setError(threadError instanceof Error ? threadError.message : String(threadError));
      }
    },
    [client, artifactId, isBusy, assignThreadId, loadHistory],
  );

  // --- the turn -------------------------------------------------------------------------
  const runTurn = useCallback(
    async (prompt: string, attachments?: Attachment[]): Promise<boolean> => {
      const text = prompt.trim();
      if (!text || !client || !token || !orgId || !targetSessionId || rfaBindingState !== "ready" || isBusy) return false;

      const turnRequest = {};
      turnRequestRef.current = turnRequest;
      const isCurrentTurn = () => identityRef.current === openingIdentity && turnRequestRef.current === turnRequest;
      let turnSucceeded = false;
      const replyId = nextId("a");
      const sent = (attachments ?? []).map(sentAttachment);
      setStatus("running");
      setError(null);
      setParameterExecutionError(null);
      setMessages((current) => [
        ...current,
        { id: nextId("u"), role: "user", text, ...(sent.length ? { attachments: sent } : {}) },
        { id: replyId, role: "assistant", text: "" },
      ]);
      const patchReply = (change: (message: FamilyMessage) => FamilyMessage) => {
        if (!isCurrentTurn()) return;
        setMessages((current) => current.map((message) => (message.id === replyId ? change(message) : message)));
      };
      const controller = new AbortController();
      abortRef.current = controller;
      let streamFailure: string | null = null;

      try {
        await streamFamilyTurn(
          {
            artifactId,
            targetSessionId,
            organizationId: orgId,
            accessToken: token,
            prompt: text,
            projectId,
            userId,
            ...(attachments?.length ? { attachments } : {}),
            ...(model ? { llmProvider: model.provider, llmModel: model.model } : {}),
            ...(reasoningEffort ? { reasoningEffort } : {}),
            abortSignal: controller.signal,
          },
          {
            onThreadInfo: (id) => {
              if (isCurrentTurn()) assignThreadId(id);
            },
            onToken: (content) => patchReply((m) => ({ ...m, text: m.text + content })),
            onReasoning: (content) => patchReply((m) => ({ ...m, reasoning: (m.reasoning ?? "") + content })),
            // The family's tools in the user's words — v2's activity rows — on the chat's step rows.
            onSteps: (steps: AgentStep[]) =>
              patchReply((m) => ({
                ...m,
                steps: steps.map((step) => (step.label ? step : { ...step, label: tRef.current(activityLabelKey(step.tool)) })),
              })),
            onInterrupt: () => undefined,
            onContextUsage: (next) => {
              if (isCurrentTurn()) setUsage(next);
            },
            onStreamError: (code, message, traceId, details) => {
              const failure = bindingFailure(Object.assign(new Error(message), { code, details }));
              if (isCurrentTurn() && failure.isBindingError) applyBindingFailure(failure);
              // The family's own codes carry the agent's sentence; the chat's codes their catalogue one.
              const documentErrorKey = familyDocumentErrorKey(message, code);
              streamFailure = documentErrorKey
                ? tRef.current(documentErrorKey)
                : code.startsWith("FAMILY_")
                  ? message
                  : describeAskError({ code, message, details }, tRef.current);
              patchReply((m) => ({ ...m, error: { text: streamFailure ?? message, traceId: traceId ?? null } }));
            },
            onAborted: () => {
              if (!isCurrentTurn()) return;
              setMessages((current) => [...current, { id: nextId("s"), role: "system", text: tRef.current("familyBuilder.stopped") }]);
            },
            onBuildEvidence: (evidence) => {
              if (!isCurrentTurn()) return;
              patchReply((m) => ({ ...m, evidence }));
              setVersionSaveError(
                evidence.status === "partial_success" ? evidence.message || tRef.current("familyBuilder.versioningPartialSuccess") : null,
              );
              // The Build event arrives after the versioning attempt: refresh now,
              // even when the agent carries on editing this turn.
              setVersionsKey((key) => key + 1);
              void loadLatestRun();
              void refreshView().catch((refreshError: unknown) => {
                console.warn("Failed to refresh the family view after Build", refreshError);
              });
            },
          },
        );
        if (streamFailure !== null) throw new Error(streamFailure);
        turnSucceeded = true;
      } catch (turnError) {
        if (!isCurrentTurn()) return false;
        const failure = bindingFailure(turnError);
        if (failure.isBindingError) applyBindingFailure(failure);
        const raw = turnError instanceof Error ? turnError.message : String(turnError);
        const documentErrorKey = familyDocumentErrorKey(raw, failure.code);
        const activeRfaRequired = documentErrorKey === "familyBuilder.activeRfaRequired";
        // A refusal in the chat's own taxonomy (a 402, a busy thread) gets the chat's sentence.
        const message = documentErrorKey
          ? t(documentErrorKey)
          : turnError instanceof FamilyBuilderError && !turnError.code.startsWith("FAMILY_")
            ? describeAskError(turnError, t)
            : raw;
        setMessages((current) => {
          const last = current[current.length - 1];
          const previous = current[current.length - 2];
          // Refused before anything happened because no family is open: the
          // exchange never took place, so it leaves no trace (v2).
          if (activeRfaRequired && last?.role === "assistant" && !last.text && !last.steps?.length && previous?.role === "user") {
            return current.slice(0, -2);
          }
          return current.map((m) => (m.id === replyId && !m.error ? { ...m, error: { text: message, traceId: null } } : m));
        });
        setError(message);
        setStatus("error");
      } finally {
        if (isCurrentTurn()) {
          abortRef.current = null;
          // A turn may save a version before ending on an agent error. Always
          // reconcile the editor with disk instead of leaving stale code and versions.
          const [viewRefresh] = await Promise.allSettled([refreshView()]);
          setVersionsKey((key) => key + 1);
          if (isCurrentTurn()) {
            // The reply that never said anything takes no room.
            setMessages((current) =>
              current.filter((m) => m.id !== replyId || m.text.trim() || m.steps?.length || m.reasoning || m.error || m.evidence),
            );
            const refreshStatus = successfulTurnRefreshStatus(turnSucceeded, viewRefresh);
            if (refreshStatus === "error") setError(t("familyBuilder.viewRefreshFailed"));
            if (refreshStatus) setStatus(refreshStatus);
            turnRequestRef.current = null;
          }
        }
      }
      return turnSucceeded;
    },
    [client, token, orgId, targetSessionId, projectId, userId, model, reasoningEffort, artifactId, openingIdentity, rfaBindingState, isBusy, t, assignThreadId, applyBindingFailure, refreshView, loadLatestRun],
  );

  const stop = useCallback(() => {
    abortRef.current?.abort();
    const id = threadIdRef.current;
    if (id && tokenRef.current) void abortTurn(id, tokenRef.current).catch(() => undefined);
  }, []);

  // --- parameters -------------------------------------------------------------------------
  const applyParameters = useCallback(async (): Promise<boolean> => {
    if (!client || rfaBindingState !== "ready" || isBusy) return false;
    const identity = openingIdentity;
    try {
      setIsApplyingParameters(true);
      setError(null);
      setParameterExecutionError(null);
      const response = await client.execute(artifactId, parameterValues(parameters));
      if (identityRef.current !== identity) return false;
      applyView(response.view);
      setLastExecution(response);
      setNotice({ tone: "ok", text: t("familyBuilder.parametersApplied") });
      return true;
    } catch (executionError) {
      if (identityRef.current !== identity) return false;
      const failure = bindingFailure(executionError);
      if (failure.isBindingError) applyBindingFailure(failure);
      const raw = executionError instanceof Error ? executionError.message : String(executionError);
      const documentErrorKey = familyDocumentErrorKey(raw, failure.code);
      const message = documentErrorKey ? t(documentErrorKey) : raw;
      setParameterExecutionError(message);
      setNotice({ tone: "err", text: message });
      return false;
    } finally {
      setIsApplyingParameters(false);
      // A failed Apply is a recorded run too, and the summary shows it as v2 did.
      if (identityRef.current === identity) void loadLatestRun();
    }
  }, [client, artifactId, openingIdentity, parameters, rfaBindingState, isBusy, t, applyView, applyBindingFailure, loadLatestRun]);

  const updateParameter = useCallback((key: string, value: FamilyParameter["value"]) => {
    setParameters((current) => current.map((parameter) => (parameter.key === key ? { ...parameter, value } : parameter)));
  }, []);

  // --- versions -------------------------------------------------------------------------
  const [restoringVersion, setRestoringVersion] = useState<number | null>(null);
  const restoreVersion = useCallback(
    async (version: number) => {
      if (!client || isBusy || restoringVersion !== null) return;
      setRestoringVersion(version);
      try {
        const restored = await client.restore(artifactId, version);
        applyView(restored);
        await refreshView().catch(() => false);
        setVersionsKey((key) => key + 1);
        // Restore puts the code back; it does not rebuild the RFA (v2) — say so.
        setNotice({ tone: "ok", text: t("familyBuilder.restored", { version }) });
      } catch (restoreError) {
        setNotice({ tone: "err", text: restoreError instanceof Error ? restoreError.message : String(restoreError) });
      } finally {
        setRestoringVersion(null);
      }
    },
    [client, artifactId, isBusy, restoringVersion, t, applyView, refreshView],
  );

  return {
    artifactId,
    client,
    threadId,
    messages,
    view,
    parameters,
    status,
    error,
    parameterExecutionError,
    versionSaveError: versionSaveError || (view?.versionHistoryIncomplete ? t("familyBuilder.versioningPartialSuccess") : null),
    lastExecution,
    latestRun,
    rfaBinding,
    rfaBindingState,
    rfaBindingError,
    rfaBindingPath,
    rfaBindingRetryAction,
    rfaBindingCode,
    isRfaReady: rfaBindingState === "ready",
    isRunning,
    isApplyingParameters,
    isBusy,
    isLoadingHistory: status === "loading_history",
    hasParameterChanges: computeParameterChanges(view, parameters),
    usage,
    versionsKey,
    restoringVersion,
    notice,
    clearNotice: () => setNotice(null),
    updateParameter,
    runTurn,
    applyParameters,
    stop,
    newThread,
    switchThread,
    refreshView,
    openRfaInRevit,
    restoreVersion,
  };
}

export type FamilyBuilderController = ReturnType<typeof useFamilyBuilder>;
